import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { all, call, put, PutEffect, select, takeLatest } from 'redux-saga/effects'
import { RootState } from 'state/store'

import api from '@/api/_legacy'
import { ApiObservationFrequency, ApiStrategyTemplate } from '@/api/_legacy/generated-api'
import { createLogoutExtraReducers } from '@/utilities/redux'

import { Errors, extractErrorMessagesFromServerResponseToArray } from '../../tradebuilder-v2/util'
import { validate } from './validation-rules'

const TENANT_ID = 'absa'

export type EditStrategiesState = {
  availableStrategies: ApiStrategyTemplate[]
  canProceed: boolean
  errors: Errors
  saveSuccess: boolean
  selectedStrategy?: ApiStrategyTemplate
  serverErrors?: string[]
}

const initialState: EditStrategiesState = {
  canProceed: false,
  saveSuccess: false,
  availableStrategies: [],
  errors: {}
}

export const editStrategiesSlice = createSlice({
  name: 'editStrategies',
  initialState,
  reducers: {
    init: () => {
      // noop
    },
    saveError: (state, action: PayloadAction<any>) => {
      state.serverErrors = extractErrorMessagesFromServerResponseToArray(action.payload)
    },
    fetchStrategiesSuccess: (state, action: PayloadAction<ApiStrategyTemplate[]>) => {
      state.availableStrategies = action.payload
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    saveStrategy: () => {
      // eslint-disable-next-line no-console
      console.log('saving strategy test...')
    },
    setSaveSuccessFalse: state => {
      state.saveSuccess = false
    },
    saveStrategySuccess: state => {
      state.canProceed = false
      state.saveSuccess = true
    },
    setSelectedStrategy: (state, action: PayloadAction<string>) => {
      state.selectedStrategy = state.availableStrategies.find(
        x => x.strategyTemplateId === action.payload
      )
      state.saveSuccess = false
      state.errors = {}
    },
    setMinLeverageRatio: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.minLeverageRatio = action.payload
      state.canProceed = true
      delete state.errors.leverage_ratios
      delete state.errors.min_lev_ratio
    },
    setMaxLeverageRatio: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.maxLeverageRatio = action.payload
      state.canProceed = true
      delete state.errors.leverage_ratios
      delete state.errors.max_lev_ratio
    },
    setDefaultLeverageRatio: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.defaultLeverageRatio = action.payload
      state.canProceed = true
      delete state.errors.leverage_ratios
    },
    setMinProtectionLevel: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.minProtectionLevel = action.payload
      state.canProceed = true
      delete state.errors.prot_levels
    },
    setMaxProtectionLevel: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.maxProtectionLevel = action.payload
      state.canProceed = true
      delete state.errors.prot_levels
      delete state.errors.max_cts_prot_level
      delete state.errors.max_term_prot_level
    },
    setDefaultProtectionLevel: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.defaultProtectionLevel = action.payload
      state.canProceed = true
      delete state.errors.prot_levels
    },
    setDefaultHWMFreq: (state, action: PayloadAction<ApiObservationFrequency>) => {
      state.selectedStrategy.defaultHighWatermarkObservationFrequency = action.payload
      state.canProceed = true
    },
    setMinTargetVol: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.minTargetVolatility = action.payload
      state.canProceed = true
      delete state.errors.target_vols
      delete state.errors.min_target_vol
    },
    setMaxTargetVol: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.maxTargetVolatility = action.payload
      state.canProceed = true
      delete state.errors.target_vols
      delete state.errors.max_target_vol
    },
    setDefaultTargetVol: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.defaultTargetVolatility = action.payload
      state.canProceed = true
      delete state.errors.target_vols
    },
    setVolIndexTriggerLevel: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.volatilityIndexTriggerLevel = action.payload
      state.canProceed = true
      delete state.errors.vol_index_trigger
    },
    setShortCircuitMaxAlloc: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.shortCircuitMaxAllocation = action.payload
      state.canProceed = true
      delete state.errors.short_circuit
    },
    setShortCircuitReducedMultiplier: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.shortCircuitReducedMultiplier = action.payload
      state.canProceed = true
      delete state.errors.reduced_multiplier
    },
    setMinNumMonthsToMaturity: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.minNumberOfMonthsToTimeToMaturity = action.payload
      state.canProceed = true
      delete state.errors.maturity_months
      delete state.errors.min_maturity_month
    },
    setMaxNumMonthsToMaturity: (state, action: PayloadAction<number>) => {
      state.selectedStrategy.maxNumberOfMonthsToTimeToMaturity = action.payload
      state.canProceed = true
      delete state.errors.maturity_months
      delete state.errors.max_maturity_month
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

export const { actions } = editStrategiesSlice
export type EditStrategiesActions = typeof actions

function* fetchStrategyTemplatesSaga() {
  try {
    const response = yield call(api.tenant.strategyDetail, TENANT_ID)
    yield put({
      type: 'editStrategies/fetchStrategiesSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'editStrategies/fetchStrategiesFailure', payload: error })
  }
}

function saveStrategySaga(onSuccess: PutEffect, onFail: (e: any) => PutEffect) {
  return function* saveAsset2Saga() {
    const state: RootState = yield select()
    const { editStrategies } = state
    const { selectedStrategy } = editStrategies

    const result = validate(editStrategies)
    if (!result.isValid) {
      yield put(actions.setInvalid(result.errors))
    } else {
      try {
        if (!selectedStrategy.strategyTemplateId) {
          throw new Error('FATAL ERROR: Strategy Template Id does not exist')
        }
        yield call(api.tenant.strategyUpdate, TENANT_ID, selectedStrategy)
        yield onSuccess
      } catch (error) {
        yield onFail(error)
      }
    }
  }
}

export function* editStrategyRootSaga() {
  yield all([
    takeLatest('editStrategies/init', fetchStrategyTemplatesSaga),
    takeLatest(
      'editStrategies/saveStrategy',
      saveStrategySaga(put(actions.saveStrategySuccess()), e => put(actions.saveError(e)))
    ),
    takeLatest('editStrategies/saveStrategySuccess', fetchStrategyTemplatesSaga)
  ])
}

export default editStrategiesSlice.reducer
